import React, { useRef, useState, useEffect } from 'react';
import { useSpring, animated, config } from '@react-spring/web';
import { ReactComponent as HeartIcon } from 'images/new-icons/black-heart.svg';
import { ReactComponent as HeartFilledIcon } from 'images/new-icons/filled-heart.svg';
import { ReactComponent as TrashIcon } from 'images/new-icons/black-trash.svg';
import { ReactComponent as PlusIcon } from 'images/new-icons/plus-btn.svg';
import { ReactComponent as MinusIcon } from 'images/new-icons/minus-btn.svg';
import { ReactComponent as Star } from 'images/new-icons/yellow-star.svg';
import { ReactComponent as HalfStar } from 'images/new-icons/half-yellow-star.svg';
import { ReactComponent as GreyStar } from 'images/new-icons/grey-star.svg';
import ArrowDownGray from 'images/new-icons/arrow-down-gray.svg';
import s from './ShoppingCart.module.scss';
import useGetProductVariationsByProductId from 'sdk/hooks/shop/useGetProductVariationsByProductId';
import Preloader from 'components/shared/Preloader';
import useIsAuth from 'sdk/hooks/useIsAuth';
import useIsProductFavorite from 'sdk/hooks/shop/useIsProductFavorite';
import useToggleProductFavorite from 'sdk/hooks/shop/useToggleProductFavorite';
import useGetProductReviewsByProductId from 'sdk/hooks/shop/useGetProductReviewsByProductId';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as RiyalIcon } from 'images/new-icons/riyal.svg';

const CartItemRow = ({ item, onPlus, onMinus, onRemove, isLoading }: any) => {
  const { data: isAuthData } = useIsAuth();
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const touchStartTime = useRef(0);
  const [{ x }, api] = useSpring(() => ({ x: 0 }));
  const { data: productVariations } = useGetProductVariationsByProductId(item?.product?.id);
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState<any | null>(null);
  const [showColorDropdown, setShowColorDropdown] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const { data: isFavorited } = useIsProductFavorite(item?.product?.id, isAuthData?.user?.id);
  const { mutate: toggleFavorite } = useToggleProductFavorite();
  const { data: reviews } = useGetProductReviewsByProductId(item?.product?.id);
  const [heartIsLoading, setHeartIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (productVariations?.length) {
      const storedVariation = productVariations.find(
        v =>
          v.amount === item.selected_size?.amount &&
          v.measure?.name === item.selected_size?.measure?.name,
      );
      setSelectedVariation(storedVariation || productVariations[0]);

      setSelectedColor(item.selected_color || productVariations[0]?.color);
    }
  }, [productVariations, item]);

  const hasVariations = productVariations && productVariations.length > 0;

  const handleTouchStart = e => {
    touchStartX.current = e.touches[0].clientX;
    touchStartTime.current = Date.now(); // Время начала свайпа
  };

  const handleVariationSelect = variation => {
    setSelectedVariation(variation);
    setShowSizeDropdown(false);
  };

  const handleTouchMove = e => {
    touchEndX.current = e.touches[0].clientX;
    const deltaX = touchEndX.current - touchStartX.current;
    if (deltaX < 0) {
      api.start({ x: deltaX > -120 ? deltaX : -120, config: { duration: 0 } }); // Реагируем быстро
    }
  };

  const handleTouchEnd = () => {
    const touchDuration = Date.now() - touchStartTime.current; // Длительность свайпа
    const deltaX = touchEndX.current - touchStartX.current;
    const swipeSpeed = Math.abs(deltaX) / touchDuration; // Скорость свайпа (пиксели/мс)

    if (deltaX < -50 && swipeSpeed > 0.5) {
      // Если свайп быстрый и больше 50px
      api.start({ x: -120, config: config.gentle });
    } else {
      api.start({ x: 0, config: config.wobbly });
    }
  };

  const handleColorSelect = (color: string | null) => {
    setSelectedColor(color);
    setShowColorDropdown(false);
  };

  const handleFavorite = () => {
    if (!isAuthData?.user?.id) {
      navigate('/sign-up');
      return;
    }

    setHeartIsLoading(true);

    toggleFavorite(
      { productId: item?.product?.id, userId: isAuthData?.user?.id },
      {
        onSuccess: () => {
          setHeartIsLoading(false);
        },
        onError: error => {
          console.error('Error toggling favorite:', error);
          setHeartIsLoading(false);
        },
      },
    );
  };

  const renderStars = () => {
    const stars: JSX.Element[] = [];
    let remainingRank =
      reviews?.map((review: any) => review?.rank).reduce((a: number, b: number) => a + b, 0) /
      (reviews?.length || 1);

    for (let i = 0; i < 5; i++) {
      if (remainingRank >= 1) {
        stars.push(<Star key={i} />);
        remainingRank -= 1;
      } else if (remainingRank > 0) {
        stars.push(<HalfStar key={i} />);
        remainingRank = 0;
      } else {
        stars.push(<GreyStar key={i} />);
      }
    }

    return stars;
  };

  return (
    <div className={s.cart_item_wrapper}>
      {/* Кнопки удаления */}
      <div className={s.cart_item_actions}>
        <button className={s.action_button} onClick={handleFavorite}>
          
          {isFavorited ? (
            heartIsLoading ? (
              <Preloader inline />
            ) : (
              <HeartFilledIcon />
            )
          ) : heartIsLoading ? (
            <Preloader inline />
          ) : (
            <HeartIcon />
          )}
        </button>
    <button className={s.action_button} onClick={() => onRemove(item)}>
  {isLoading ? <Preloader inline /> : <TrashIcon />}
</button>

      </div>

      {/* Свайп-контейнер */}
      <animated.div
        className={s.cart_item_card}
        style={{ transform: x.to(val => `translateX(${val}px)`) }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          className={s.cart_item_card_image}
          src={item?.product?.image ?? item?.service?.image}
          alt={item?.product?.name ?? item?.service?.name}
          onClick={e => {
            e.stopPropagation();
            window.location.href = `/product/${item?.product?.id}`;
          }}
        />
        <div className={s.cart_item_card_info}>
          <div
            className={s.product_name}
            onClick={e => {
              e.stopPropagation();
              window.location.href = `/product/${item?.product?.id}`;
            }}
          >
            {item?.product?.name ?? item?.service?.name}
          </div>
          <div className={s.product_reviews}>
            <div className={s.product_reviews_stars}>{renderStars()}</div>
            <div className={s.product_reviews_count}>{reviews?.length} reviews</div>
          </div>
          <div className={s.product_description}>
            {item?.product?.description ?? item?.service?.description}
          </div>
          <div className={s.product_attributes}>
            {hasVariations && (
              <div className={s.product_attributes_item}>
                <div className={s.product_attributes_item_title}>Size:</div>
                <div
                  className={s.product_attributes_item_value}
                  onClick={() => setShowSizeDropdown(!showSizeDropdown)}
                >
                  {selectedVariation?.amount} {selectedVariation?.measure?.name || 'N/A'}
                  <img src={ArrowDownGray} alt="ArrowDownGray" />
                </div>
                {productVariations.length > 1 && showSizeDropdown && (
                  <div className={s.dropdown_menu}>
                    {productVariations.map(variation => (
                      <div
                        key={variation.id}
                        className={s.dropdown_item}
                        onClick={() => handleVariationSelect(variation)}
                      >
                        {variation.amount} {variation.measure?.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className={s.product_attributes_item}>
              <div className={s.product_attributes_item_title2}>Color:</div>
              <div
                className={s.product_attributes_item_value}
                onClick={() => setShowColorDropdown(!showColorDropdown)}
              >
                {selectedColor || 'Select color'}
                <img src={ArrowDownGray} alt="ArrowDownGray" />
              </div>
              {showColorDropdown && (
                <div className={s.dropdown_menu}>
                  {Array.from(new Set(productVariations?.map(v => v.color)))
                    .filter(Boolean)
                    .map(color => (
                      <div
                        key={color}
                        className={s.dropdown_item}
                        onClick={() => handleColorSelect(color)}
                      >
                        {color}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
          <div className={s.actions}>
            <PlusIcon onClick={() => onPlus(item)} />
            <div className={s.actions_count}>{item.actions_count ?? 1}</div>
            <MinusIcon onClick={() => onMinus(item)} />
            <div className={s.actions_price} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RiyalIcon style={{ width: '14px', height: '14px' }} />
              {item.actions_price.toFixed(2)}
            </div>
          </div>
        </div>
      </animated.div>
    </div>
  );
};

export default CartItemRow;
